import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';

import IdsElement from '../../core/ids-element';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import { breakpoints } from '../../utils/ids-breakpoint-utils/ids-breakpoint-utils';

import '../ids-modal/ids-modal';
import '../ids-toolbar/ids-toolbar';
import '../ids-button/ids-button';
import '../ids-icon/ids-icon';
import './ids-media-viewer';

import styles from './ids-media-gallery.scss';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import type IdsButton from '../ids-button/ids-button';
import type IdsModal from '../ids-modal/ids-modal';
import type IdsScrollView from '../ids-scroll-view/ids-scroll-view';

interface ZoomPanController {
  zoom: (params: { deltaScale: number; x: number; y: number }) => void;
  panBy: (params: { originX: number; originY: number }) => void;
  panTo: (params: { originX: number; originY: number; scale: number }) => void;
  getScale: () => number;
  isZoomed: () => boolean;
}

const Base = IdsLocaleMixin(
  IdsEventsMixin(
    IdsElement
  )
);

/**
 * IDS media gallery component
 * @type {IdsMediaGallery}
 * @inherits IdsElement
 * @part gallery - the gallery container element
 */
@customElement('ids-media-gallery')
@scss(styles)
export default class IdsMediaGallery extends Base {
  constructor() {
    super();
  }

  #isDragging = false;

  #isHovering = false;

  #zoomOutButton: Element | any;

  #zoomFitButton: Element | any;

  #DEFAULT_ZOOM_STEP = 0.5;

  #MIN_SCALE = 0.1;

  #MAX_SCALE = 30;

  #ZOOM_MODE = 'center';

  /**
   * Timestamp of last wheel navigation to prevent rapid scrolling
   */
  #lastWheelNavigationTime = 0;

  /** MutationObserver for navigation child changes */
  #navigationObserver: MutationObserver | null = null;

  /**
   * Invoked each time the custom element is appended into a document-connected element.
   */
  connectedCallback() {
    super.connectedCallback();
    this.#attachEventHandlers();
    this.#applyVideoThumbClass();
    this.#updateGalleryCounter(0);
    this.#observeNavigationChanges();
  }

  /**
   * Called after the component is fully mounted and child shadow DOMs are ready
   */
  mountedCallback(): void {
    super.mountedCallback?.();
    this.#syncHoverImage();
    this.#observeNavigationChanges();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    this.#navigationObserver?.disconnect();
    this.#navigationObserver = null;
    this.#cleanupEventHandlers();
  }

  /**
   * Observes the navigation scroll view for child list changes.
   * When thumbnails are dynamically added or removed (e.g. Angular @for re-render),
   * re-binds event handlers and updates the gallery counter.
   */
  #observeNavigationChanges(): void {
    // Disconnect any existing observer
    this.#navigationObserver?.disconnect();

    const scrollView = this.querySelector('[slot="media-gallery-navigation"]');
    if (!scrollView) {
      // If scroll view doesn't exist yet, observe self for it being added
      this.#navigationObserver = new MutationObserver(() => {
        const sv = this.querySelector('[slot="media-gallery-navigation"]');
        if (sv) {
          this.#navigationObserver?.disconnect();
          this.#observeNavigationChanges();
        }
      });
      this.#navigationObserver.observe(this, { childList: true });
      return;
    }

    this.#navigationObserver = new MutationObserver(() => {
      // Clean up old thumbnail event handlers
      const thumbCount = this.thumbnails?.length || 0;
      for (let i = 0; i < thumbCount; i++) {
        this.offEvent(`click.thumb-${i}`);
        this.offEvent(`keydown.thumb-${i}`);
        this.offEvent(`mouseenter.thumb-${i}`);
      }

      // Re-bind thumbnail events to new elements
      const image: Element | undefined | null = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image');
      const instance = this.#initZoomController(image);
      this.#setupThumbnailEvents(instance);
      this.#applyVideoThumbClass();
      this.#updateGalleryCounter(0);
      this.#syncHoverImage();
    });

    this.#navigationObserver.observe(scrollView, { childList: true, subtree: true });
  }

  /**
   * Clean up all event handlers
   * @private
   * @returns {void}
   */
  #cleanupEventHandlers(): void {
    this.offEvent('resize.mediagallery', window);
    this.offEvent('wheel.gallery');
    this.offEvent('click.expand');
    this.offEvent('click.zoomin');
    this.offEvent('click.zoomout');
    this.offEvent('click.zoomfit');
    this.offEvent('dblclick.image');
    this.offEvent('wheel.imagezoom');
    this.offEvent('mouseenter.image');
    this.offEvent('mousedown.image');
    this.offEvent('mouseup.image');
    this.offEvent('mouseleave.image');
    this.offEvent('mousemove.image');
    this.offEvent('wheel.thumbscroll');

    // Clean up indexed thumbnail events
    const thumbCount = this.thumbnails?.length || 0;
    for (let i = 0; i < thumbCount; i++) {
      this.offEvent(`click.thumb-${i}`);
      this.offEvent(`keydown.thumb-${i}`);
      this.offEvent(`mouseenter.thumb-${i}`);
    }

    // Clean up indexed touch gesture events
    const mediaViewer = this.querySelector('ids-media-viewer')?.shadowRoot;
    const gestureElements = [
      mediaViewer?.querySelector('ids-image'),
      mediaViewer?.querySelector('.ids-media-viewer-video')
    ].filter(Boolean);

    gestureElements.forEach((_, index) => {
      ['touchstart', 'touchmove', 'touchend'].forEach((event) => {
        this.offEvent(`${event}.gesture-${index}`);
      });
    });

    // Clean up circle button events
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;
    const circleCount = scrollView?.circleButtons?.length || 0;
    for (let i = 0; i < circleCount; i++) {
      this.offEvent(`click.circle-${i}`);
    }
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The attributes in an array
   */
  static get attributes(): Array<string> {
    return [
      ...super.attributes,
      attributes.SUBTITLE,
      attributes.ENABLE_HOVER_CHANGE,
      attributes.DISABLE_MOBILE_ZOOM
    ];
  }

  /**
   * Set the enableHoverChange attribute
   * @param {boolean} val - true to enable hover change, false to disable
   */
  set enableHoverChange(val: string | boolean) {
    const isTruthy = stringToBool(val);
    if (isTruthy) {
      this.setAttribute(attributes.ENABLE_HOVER_CHANGE, '');
    } else {
      this.removeAttribute(attributes.ENABLE_HOVER_CHANGE);
    }
  }

  get enableHoverChange(): boolean {
    return this.hasAttribute(attributes.ENABLE_HOVER_CHANGE);
  }

  /**
   * Set the disableMobileZoom attribute
   * @param {boolean} val - true to disable zoom on mobile, false to enable
   */
  set disableMobileZoom(val: string | boolean) {
    const isTruthy = stringToBool(val);
    if (isTruthy) {
      this.setAttribute(attributes.DISABLE_MOBILE_ZOOM, '');
    } else {
      this.removeAttribute(attributes.DISABLE_MOBILE_ZOOM);
    }
  }

  get disableMobileZoom(): boolean {
    return this.hasAttribute(attributes.DISABLE_MOBILE_ZOOM);
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    const markup = `<div class="ids-media-gallery" part="media-gallery">
        <slot name="media-gallery-toolbar">
          <ids-toolbar>
            <ids-toolbar-section type="title">
              <ids-text id="media-gallery-counter" class="media-gallery-counter" color="unset" font-size="14" translate-text="true"></ids-text>
            </ids-toolbar-section>

            <ids-toolbar-section type="buttonset" align="end">
              <ids-button icon="zoom-in" tooltip="Zoom In">
                <ids-text audible translate-text="true">Zoom in</ids-text>
              </ids-button>
              <ids-button icon="zoom-out" tooltip="Zoom Out" disabled>
                <ids-text audible translate-text="true">Zoom Out</ids-text>
              </ids-button>
              <ids-button icon="zoom-fit" tooltip="Zoom to Fit" disabled>
                <ids-text audible translate-text="true">Zoom Fit</ids-text>
              </ids-button>
              <ids-button id="expand-button" icon="make-same-size" tooltip="Expand">
                <ids-text audible translate-text="true">Expand</ids-text>
              </ids-button>
            </ids-toolbar-section>
          </ids-toolbar>
        </slot>
        <slot name="media-gallery-viewer"></slot>
        <slot name="media-gallery-navigation"></slot>
        <div class="ids-image-zoom-lens"></div>
      </div>
      <ids-modal
        id="fullsize-image"
        aria-labelledby="fullsize-image"
        height="90vh"
        width="75vw"
        fullsize="lg"
        show-close-button
        focus-target="false"
        scrollable
      >
        <ids-text slot="title" font-size="24" type="h2" id="my-modal-title" translate-text="true">Selected Image</ids-text>
        <ids-image size="full"></ids-image>
      </ids-modal>
      `;
    return markup;
  }

  /**
   * Get the thumbnails
   * @returns {Array} The thumbnails
   */
  get thumbnails() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]');
    const thumbnails = scrollView?.querySelectorAll('[slot="scroll-view-item"]');
    return thumbnails;
  }

  get mediaViewer() {
    const mediaViewer = this.querySelector('[slot="media-gallery-viewer"]');
    return mediaViewer;
  }

  #initZoomController(element: Element | any, zoomMode = this.#ZOOM_MODE) {
    return this.createZoomPanController({
      minScale: this.#MIN_SCALE,
      maxScale: this.#MAX_SCALE,
      zoomStep: this.#DEFAULT_ZOOM_STEP,
      element,
      zoomMode
    });
  }

  #attachEventHandlers() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]');
    const thumbnails = scrollView?.querySelectorAll('[slot="scroll-view-item"]');
    const mediaViewer: any = this.querySelector('[slot="media-gallery-viewer"]');
    const expandButton = this.shadowRoot?.querySelector<IdsButton>('ids-button#expand-button');
    this.#zoomOutButton = this.shadowRoot?.querySelector('ids-button[icon="zoom-out"]');
    this.#zoomFitButton = this.shadowRoot?.querySelector('ids-button[icon="zoom-fit"]');
    const image: any = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image');
    const imageHover: any = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('#ids-image-hover');

    const instance = this.#initZoomController(image);
    const hoverInstance = this.#initZoomController(imageHover, 'cursor');

    if (!thumbnails || !mediaViewer) return;

    this.#setupThumbnailEvents(instance);

    this.#setupZoomEvents(instance);

    this.#setupPanEvents(instance, hoverInstance);

    // Add throttled wheel navigation - only when mouse is over the gallery viewer area
    const galleryEl = this.shadowRoot?.querySelector('.ids-media-gallery');
    this.onEvent('wheel.gallery', galleryEl, (event: WheelEvent) => {
      const selectedThumb = this.querySelector('[slot="media-gallery-navigation"] .selected');
      if (!selectedThumb || instance.isZoomed()) return;

      const now = Date.now();
      if (now - this.#lastWheelNavigationTime < 300) return;
      this.#lastWheelNavigationTime = now;

      event.preventDefault();
      if (event.deltaY > 0) {
        this.#navigateToNext(instance);
      } else {
        this.#navigateToPrevious(instance);
      }
    });

    this.#waitForScrollViewAndSetupNavigation().catch((e) => console.info('Media gallery navigation setup skipped:', e));

    // Setup Expand Events
    this.onEvent('click.expand', expandButton, async () => {
      const fullsizeImage = this.shadowRoot?.querySelector<IdsModal>('#fullsize-image');
      const modalImage: any = fullsizeImage?.querySelector('ids-image');
      const currentType = this.mediaViewer?.getAttribute(attributes.TYPE);

      if (modalImage && this.mediaViewer) {
        const currentSrc = this.mediaViewer.getAttribute('src');

        if (currentType === 'video') {
          // Create video element if it doesn't exist
          let modalVideo: any = fullsizeImage?.querySelector('video');
          if (!modalVideo) {
            modalVideo = document.createElement('video');
            modalVideo.setAttribute('controls', '');
            modalVideo.setAttribute('width', '100%');
            modalVideo.setAttribute('height', 'auto');
            modalVideo.style.maxHeight = '100%';
            modalVideo.style.objectFit = 'contain';
            fullsizeImage?.appendChild(modalVideo);
          }

          // Get video source from thumbnail
          const selectedThumb = this.querySelector('[slot="media-gallery-navigation"] .selected');
          const videoSrc = selectedThumb?.getAttribute('data-src') || currentSrc;

          modalVideo.src = videoSrc;
          modalVideo.style.display = 'block';
          modalImage.style.display = 'none';
        } else {
          // Hide video if it exists and show image
          const modalVideo = fullsizeImage?.querySelector('video');
          if (modalVideo) modalVideo.style.display = 'none';

          modalImage.src = currentSrc;
          modalImage.style.display = 'block';
        }
      }

      // Update modal title with current image alt text from selected thumbnail
      const modalTitle = this.shadowRoot?.querySelector('#my-modal-title');
      const selectedThumb = this.querySelector('[slot="media-gallery-navigation"] .selected');
      const currentAlt = selectedThumb?.getAttribute(attributes.ALT)
        || selectedThumb?.querySelector('img')?.getAttribute(attributes.ALT);
      if (modalTitle) {
        modalTitle.textContent = currentAlt || 'Selected Image';
      }

      await fullsizeImage?.show();
    });
  }

  #handleThumbnailClick(thumb: HTMLElement, instance: any) {
    const mediaViewer = this.mediaViewer as any;
    const hoverImage = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('#ids-image-hover');
    const video = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('.ids-media-viewer-video');
    const videoSource = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('.ids-media-viewer-video source');
    const image = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image');
    const type = thumb.getAttribute('data-type') || 'image';
    const src = thumb.getAttribute(attributes.SRC) || '';
    const videoSrc = thumb.getAttribute('data-src') || '';

    mediaViewer?.setAttribute(attributes.SRC, src);
    mediaViewer?.setAttribute(attributes.TYPE, type);

    if (type === 'video') {
      this.#disableZoomUI();
      mediaViewer.style.cursor = 'default';
      video?.setAttribute(attributes.SRC, videoSrc);
      videoSource?.setAttribute(attributes.SRC, videoSrc);
      video?.removeAttribute(attributes.HIDDEN);
      image?.setAttribute(attributes.HIDDEN, '');
    } else {
      this.#enableZoomUI();
      hoverImage?.setAttribute(attributes.SRC, src);
      video?.setAttribute(attributes.HIDDEN, '');
      image?.removeAttribute(attributes.HIDDEN);
    }

    // Selection logic
    const index = Array.from(this.thumbnails || []).indexOf(thumb);
    this.#updateGalleryCounter(index);
    this.thumbnails?.forEach((t) => t.classList.remove('selected'));
    thumb.classList.add('selected');

    // Update circle button selection for mobile
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;
    if (scrollView?.circleButtons) {
      scrollView.circleButtons.forEach((btn: IdsButton, i: number) => {
        if (i === index) {
          btn.classList.add('selected');
        } else {
          btn.classList.remove('selected');
        }
      });
    }

    this.#resetZoomAndPan(instance, image);
  }

  /**
   * Wait for scroll view component to be ready and setup responsive navigation
   */
  async #waitForScrollViewAndSetupNavigation() {
    let scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;

    if (typeof scrollView?.switchMode !== 'function') {
      await customElements.whenDefined('ids-scroll-view');
      scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;
    }

    if (typeof scrollView?.switchMode === 'function') {
      this.#setupResponsiveNavigation();
      this.#setupNavigationTooltips();
    }
  }

  /**
   * Setup responsive navigation
   */
  #setupResponsiveNavigation() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;
    const updateNavigation = () => {
      if (!scrollView || !scrollView.switchMode) return;

      const isMobile = this.#isMobileScreen();
      const isPrevCircle = !scrollView.asNav;

      scrollView.switchMode(!isMobile);

      if (isMobile && !isPrevCircle) {
        requestAnimationFrame(() => {
          this.#setupCircleButtonEvents();
        });
      }
    };

    updateNavigation();

    this.onEvent('resize.mediagallery', window, updateNavigation);
  }

  #setupCircleButtonEvents() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;

    if (!scrollView || scrollView.asNav) return;

    const circleButtons = scrollView.circleButtons;

    if (!circleButtons) return;

    circleButtons.forEach((button: IdsButton, index: number) => {
      this.offEvent(`click.circle-${index}`, button);
      this.onEvent(`click.circle-${index}`, button, () => {
        const thumbnails = this.thumbnails;
        const thumb = thumbnails?.[index];
        if (thumb) {
          const image = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image');
          const instance = this.#initZoomController(image);
          this.#handleThumbnailClick(thumb as HTMLElement, instance);
        }
      });
    });
  }

  /**
   * Setup tooltips for navigation buttons (Previous/Next)
   */
  #setupNavigationTooltips() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;

    if (!scrollView) return;

    // Add tooltips to previous/next buttons when in navigation mode
    const prevButton = scrollView.shadowRoot?.querySelector('#scroll-start');
    const nextButton = scrollView.shadowRoot?.querySelector('#scroll-end');

    if (prevButton) {
      prevButton.setAttribute(attributes.TOOLTIP, 'Previous');
    }

    if (nextButton) {
      nextButton.setAttribute(attributes.TOOLTIP, 'Next');
    }
  }

  /**
   * Setup thumnail events
   * @param {any} instance Zoom/Pan instance
   */
  #setupThumbnailEvents(instance: any) {
    const thumbnails = this.thumbnails;

    thumbnails?.forEach((thumb: any, i: number) => {
      thumb.setAttribute(attributes.TABINDEX, '0');

      this.offEvent(`click.thumb-${i}`, thumb);
      this.onEvent(`click.thumb-${i}`, thumb, () => this.#handleThumbnailClick(thumb, instance));

      // Keyboard events
      this.offEvent(`keydown.thumb-${i}`, thumb);
      this.onEvent(`keydown.thumb-${i}`, thumb, (event: KeyboardEvent) => {
        if ([' ', 'Enter'].includes(event.key)) {
          event.preventDefault();
          this.#handleThumbnailClick(thumb, instance);
        }
      });

      if (this.enableHoverChange) {
        this.offEvent(`mouseenter.thumb-${i}`, thumb);
        this.onEvent(`mouseenter.thumb-${i}`, thumb, () => this.#handleThumbnailClick(thumb, instance));
      }
    });
  }

  /**
   * Setup zoom events
   * @param {any} instance Zoom/Pan instance
   */
  #setupZoomEvents(instance: any) {
    const zoomInButton = this.shadowRoot?.querySelector('ids-button[icon="zoom-in"]');
    const image: any = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image');

    this.onEvent('click.zoomin', zoomInButton, (event: any) => {
      event.preventDefault();
      instance.zoom({
        deltaScale: Math.sign(event.deltaY) > 0 ? -1 : 1,
        x: event.pageX,
        y: event.pageY
      });

      image.style.cursor = 'grab';

      if (this.#zoomOutButton && instance.getScale() > 1) {
        this.#zoomOutButton.disabled = false;
        this.#zoomFitButton.disabled = false;
      }
    });

    this.onEvent('click.zoomout', this.#zoomOutButton, (event: any) => {
      instance.zoom({
        deltaScale: -1,
        x: event.pageX,
        y: event.pageY
      });
      this.#isDragging = false;

      this.#updateCursor(instance, image);

      if (instance.getScale() <= 1) {
        instance.panTo({
          originX: 0,
          originY: 0,
          scale: 1,
        });
        this.#zoomOutButton.disabled = true;
        this.#zoomFitButton.disabled = true;
      }
    });

    this.onEvent('dblclick.image', image, (event: any) => {
      if (instance.isZoomed()) {
        this.#resetZoomAndPan(instance, image);
      } else {
        instance.zoom({
          deltaScale: 2,
          x: event.clientX,
          y: event.clientY
        });
        this.#zoomOutButton.disabled = false;
        this.#zoomFitButton.disabled = false;
        image.style.cursor = 'grab';
      }
    });

    // Mouse wheel zoom (with navigation prevention)
    this.onEvent('wheel.imagezoom', image, (event: WheelEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const deltaScale = event.deltaY > 0 ? -1 : 1;
      instance.zoom({
        deltaScale,
        x: event.clientX,
        y: event.clientY
      });

      if (instance.getScale() > 1) {
        this.#zoomOutButton.disabled = false;
        this.#zoomFitButton.disabled = false;
        image.style.cursor = 'grab';
      } else {
        this.#zoomOutButton.disabled = true;
        this.#zoomFitButton.disabled = true;
        image.style.cursor = 'zoom-in';
      }
    });

    this.onEvent('click.zoomfit', this.#zoomFitButton, () => {
      instance.panTo({
        originX: 0,
        originY: 0,
        scale: 1,
      });
      this.#isDragging = false;
      this.#zoomOutButton.disabled = true;
      this.#zoomFitButton.disabled = true;
      this.#updateCursor(instance, image);
    });
  }

  /**
   * Setup pan events
   * @param {ZoomPanController} instance Zoom/Pan instance
   * @param {ZoomPanController} hoverInstance Zoom/Pan hover instance
   */
  #setupPanEvents(instance: ZoomPanController, hoverInstance: ZoomPanController) {
    const image = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('ids-image') as HTMLElement;
    const imageHover = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('#ids-image-hover') as HTMLElement;
    const imageHoverContainer = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('#ids-hover-zoom-container') as HTMLElement;
    const lens = this.shadowRoot?.querySelector('.ids-image-zoom-lens') as HTMLElement;

    // Mobile touch gesture support
    this.#setupMobileGestures(instance, image);

    this.onEvent('mouseenter.image', image, (event: any) => {
      event.preventDefault();

      this.#updateCursor(instance, image);

      if (!instance.isZoomed() && !this.#shouldDisableHoverZoom()) {
        this.#isHovering = true;
        hoverInstance.panTo({
          originX: 0,
          originY: 0,
          scale: 1,
        });
        lens.style.display = 'block';
        imageHoverContainer.removeAttribute('hidden');
      }
    });

    this.onEvent('mousedown.image', image, (event: any) => {
      event.preventDefault();
      if (!instance.isZoomed()) return;
      this.#isDragging = true;
      this.#isHovering = false;
      image.style.cursor = 'grabbing';
      imageHoverContainer.setAttribute('hidden', '');
    });

    this.onEvent('mouseup.image', image, (event: any) => {
      event.preventDefault();
      this.#isDragging = false;
      this.#updateCursor(instance, image);
      if (instance.isZoomed()) {
        imageHoverContainer.setAttribute('hidden', '');
      }
    });

    this.onEvent('mouseleave.image', image, (event: any) => {
      event.preventDefault();
      this.#isDragging = false;
      this.#isHovering = false;
      this.#updateCursor(instance, image);

      hoverInstance.panTo({
        originX: 0,
        originY: 0,
        scale: 1,
      });
      lens.style.display = 'none';
      imageHoverContainer.setAttribute('hidden', '');
    });

    this.onEvent('mousemove.image', image, (event: any) => {
      event.preventDefault();
      this.#updateCursor(instance, image);

      if (this.#isHovering && hoverInstance && !this.#shouldDisableHoverZoom()) {
        const imageRect: any = image.getBoundingClientRect();
        const galleryRect: any = this.container?.getBoundingClientRect();

        if (!galleryRect) return;

        const x = event.clientX - imageRect.left;
        const y = event.clientY - imageRect.top;
        const LENS_SIZE = image.clientWidth / 2;
        const CURSOR_SIZE = 15;

        lens.style.setProperty('--ids-lens-size', `${LENS_SIZE}px`);

        // Calculate the lens's top-left position, centered on the cursor
        let lensX = x - (LENS_SIZE / 2);
        let lensY = y - (LENS_SIZE / 2);

        // Clamp the lens's position to stay within the image boundaries
        lensX = Math.max(0, Math.min(lensX, imageRect.width - LENS_SIZE) - 1);
        lensY = Math.max(0, Math.min(lensY, imageRect.height - LENS_SIZE - 4));

        // Calculate the final Y position including the offset from the gallery's top
        const offsetY = imageRect.top - galleryRect.top;
        const finalLensY = lensY + offsetY;
        const finalLensX = this.localeAPI.isRTL() ? lensX - imageRect.width + LENS_SIZE : lensX;

        // Calculate the hover container dimensions
        const hoverContainer = imageHover.parentElement;
        const hoverContainerWidth = hoverContainer?.clientWidth || 0;
        const hoverContainerHeight = hoverContainer?.clientHeight || 0;
        const scale = image.clientWidth / LENS_SIZE;

        // Calculate the position in the zoomed image based on cursor position
        // Map cursor position (0 to imageRect.width/height) to zoomed image position
        const normalizedX = x / imageRect.width;
        const normalizedY = y / imageRect.height;

        // Calculate the pan offset to center the cursor position in the hover container
        const panX = (normalizedX * imageHover.clientWidth) - (hoverContainerWidth / 2);
        const panY = (normalizedY * imageHover.clientHeight) - (hoverContainerHeight / 2);

        hoverInstance.panTo({
          originX: -panX,
          originY: -panY,
          scale
        });

        lens.style.transform = `translate(${finalLensX}px, ${finalLensY - CURSOR_SIZE}px)`;
        return;
      }

      if (this.#isDragging && instance.isZoomed()) {
        image.style.cursor = 'grabbing';
        instance.panBy({
          originX: event.movementX,
          originY: event.movementY,
        });
        imageHoverContainer.setAttribute('hidden', '');
      }
    });
  }

  /**
   * Setup mobile gesture support for pinch-to-zoom and touch scrolling
   * @param {ZoomPanController} instance Zoom/Pan instance
   * @param {HTMLElement} image Image element
   */
  #setupMobileGestures(instance: ZoomPanController, image: HTMLElement) {
    if (!this.#isTouchDevice()) return;

    const video = this.querySelector('ids-media-viewer')?.shadowRoot?.querySelector('.ids-media-viewer-video') as HTMLElement;
    const elements = [image, video].filter(Boolean);

    let initialPinchDistance = 0;
    let initialZoomScale = 1;
    let previousTouchX = 0;
    let previousTouchY = 0;
    let lastDoubleTapTime = 0;
    let isPinchGestureActive = false;
    let touchStartX = 0;
    let touchStartY = 0;
    let isHorizontalSwipe = false;

    const calculateTouchDistance = (touches: TouchList) => {
      if (touches.length < 2) return 0;
      const horizontalDistance = touches[1].clientX - touches[0].clientX;
      const verticalDistance = touches[1].clientY - touches[0].clientY;
      return Math.sqrt(horizontalDistance * horizontalDistance + verticalDistance * verticalDistance);
    };

    const calculateTouchCenter = (touches: TouchList) => {
      if (touches.length < 2) return { x: touches[0].clientX, y: touches[0].clientY };
      return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2
      };
    };

    elements.forEach((element, i) => {
      this.onEvent(`touchstart.gesture-${i}`, element, (event: TouchEvent) => {
        if (event.touches.length === 2) {
          event.preventDefault();
          isPinchGestureActive = true;
          initialPinchDistance = calculateTouchDistance(event.touches);
          initialZoomScale = instance.getScale();
        } else if (event.touches.length === 1) {
          const touch = event.touches[0];
          previousTouchX = touch.clientX;
          previousTouchY = touch.clientY;
          touchStartX = touch.clientX;
          touchStartY = touch.clientY;
          isHorizontalSwipe = !instance.isZoomed() || element === video;
        }
      });
    });

    elements.forEach((element, i) => {
      this.onEvent(`touchmove.gesture-${i}`, element, (event: TouchEvent) => {
        if (event.touches.length === 2 && isPinchGestureActive) {
          event.preventDefault();
          const currentPinchDistance = calculateTouchDistance(event.touches);
          if (initialPinchDistance > 0) {
            const newScale = (currentPinchDistance / initialPinchDistance) * initialZoomScale;
            const clampedScale = Math.max(this.#MIN_SCALE, Math.min(this.#MAX_SCALE, newScale));
            const pinchCenter = calculateTouchCenter(event.touches);

            instance.zoom({
              deltaScale: Math.sign(clampedScale - instance.getScale()) * this.#DEFAULT_ZOOM_STEP,
              x: pinchCenter.x,
              y: pinchCenter.y
            });

            if (instance.getScale() > 1) {
              this.#zoomOutButton.disabled = false;
              this.#zoomFitButton.disabled = false;
            }
          }
        } else if (event.touches.length === 1 && instance.isZoomed() && !isPinchGestureActive) {
          event.preventDefault();
          const currentTouch = event.touches[0];
          const panDeltaX = currentTouch.clientX - previousTouchX;
          const panDeltaY = currentTouch.clientY - previousTouchY;

          instance.panBy({ originX: panDeltaX, originY: panDeltaY });

          previousTouchX = currentTouch.clientX;
          previousTouchY = currentTouch.clientY;
        }
      });
    });

    elements.forEach((element, i) => {
      this.onEvent(`touchend.gesture-${i}`, element, (event: TouchEvent) => {
        if (event.touches.length === 0) {
          if (isPinchGestureActive) {
            isPinchGestureActive = false;
            if (instance.getScale() <= 1.1) {
              this.#resetZoomAndPan(instance, image);
            }
          } else if (event.changedTouches.length === 1) {
            const touch = event.changedTouches[0];

            // Handle swipe navigation when not zoomed (or for videos)
            if (isHorizontalSwipe && (!instance.isZoomed() || element === video)) {
              const touchEndX = touch.clientX;
              const touchEndY = touch.clientY;
              const deltaX = touchEndX - touchStartX;
              const deltaY = touchEndY - touchStartY;
              const minSwipeDistance = 50;

              // Check if it's a horizontal swipe (more horizontal than vertical movement)
              if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > minSwipeDistance) {
                event.preventDefault();
                if (deltaX > 0) {
                  this.#navigateToPrevious(instance);
                } else {
                  this.#navigateToNext(instance);
                }
              }
            }

            // Handle double tap zoom (only for images, not videos)
            if (element === image) {
              const currentTapTime = Date.now();
              const doubleTapThreshold = this.#getDoubleTapThreshold();
              if (currentTapTime - lastDoubleTapTime < doubleTapThreshold) {
                event.preventDefault();
                if (instance.isZoomed()) {
                  this.#resetZoomAndPan(instance, image);
                } else {
                  instance.zoom({
                    deltaScale: 2,
                    x: touch.clientX,
                    y: touch.clientY
                  });
                  this.#zoomOutButton.disabled = false;
                  this.#zoomFitButton.disabled = false;
                }
              }
              lastDoubleTapTime = currentTapTime;
            }
          }

          isHorizontalSwipe = false;
        }
      });
    });

    this.#setupTouchScrolling();
    // this.#setupMobileNavigationButtons();
  }

  /**
   * Navigate to adjacent image - moves one item at a time
   * @param {ZoomPanController} instance Zoom/Pan instance
   * @param {number} direction 1 for next, -1 for previous
   */
  #navigateToImage(instance: ZoomPanController, direction: number) {
    const selectedThumb = this.querySelector('[slot="media-gallery-navigation"] .selected');
    if (!selectedThumb) return;

    const thumbsList = Array.from(this.thumbnails || []);
    const currentIndex = thumbsList.indexOf(selectedThumb);
    let nextIndex = currentIndex + direction;

    // Clamp to bounds instead of wrapping
    if (nextIndex >= thumbsList.length) nextIndex = thumbsList.length - 1;
    if (nextIndex < 0) nextIndex = 0;

    // Only navigate if we're not already at the boundary
    if (nextIndex !== currentIndex) {
      const targetThumb = thumbsList[nextIndex] as HTMLElement;
      if (targetThumb) {
        this.#handleThumbnailClick(targetThumb, instance);
      }
    }
  }

  /**
   * Navigate to next image
   * @param {ZoomPanController} instance Zoom/Pan instance
   */
  #navigateToNext(instance: ZoomPanController) {
    this.#navigateToImage(instance, 1);
  }

  /**
   * Navigate to previous image
   * @param {ZoomPanController} instance Zoom/Pan instance
   */
  #navigateToPrevious(instance: ZoomPanController) {
    this.#navigateToImage(instance, -1);
  }

  /**
   * Setup horizontal scrolling for thumbnail navigation
   */
  #setupTouchScrolling() {
    const scrollView = this.querySelector('[slot="media-gallery-navigation"]') as IdsScrollView;
    if (!scrollView) return;

    const scrollContainer = scrollView.shadowRoot?.querySelector('.ids-scroll-view') as HTMLElement;
    if (!scrollContainer) return;

    scrollContainer.classList.add('ids-media-gallery-horizontal-scroll');

    this.thumbnails?.forEach((thumb: Element) => {
      (thumb as HTMLElement).classList.add('ids-media-gallery-thumbnail');
    });

    // Desktop: Mouse wheel scrolling (both regular and shift)
    this.onEvent('wheel.thumbscroll', scrollContainer, (event: WheelEvent) => {
      // Only handle wheel events that originate from within the scroll container
      if (!scrollContainer.contains(event.target as Node)) return;

      event.preventDefault();
      const delta = event.deltaY || event.deltaX;
      scrollContainer.scrollLeft += delta;
    });
  }

  /**
   * Disable ZoomUI
   */
  #disableZoomUI() {
    this.#zoomOutButton.disabled = true;
    this.#zoomFitButton.disabled = true;
    this.shadowRoot?.querySelector('ids-button[icon="zoom-in"]')?.setAttribute('disabled', 'true');
    this.#isHovering = false;
    this.#isDragging = false;
  }

  /**
   * Enable ZoomUI
   */
  #enableZoomUI() {
    this.shadowRoot?.querySelector('ids-button[icon="zoom-in"]')?.removeAttribute('disabled');
  }

  /**
   * Set the cursor
   * @param {Element | any} el Zoom/Pan element
   * @param {any} state isZoomed
   */
  #setCursor(el: Element | any, state: any) {
    el.style.cursor = state;
  }

  /**
   * Update the cursor
   * @param {any} instance Zoom/Pan instance
   * @param {Element | any} el Zoom/Pan element
   */
  #updateCursor(instance: any, el: Element | any) {
    this.#setCursor(el, instance.isZoomed() ? 'grab' : 'zoom-in');
  }

  /**
   * Sync the hover image src with the current media viewer src
   * so the larger view is available on first hover
   */
  #syncHoverImage(): void {
    const mediaViewer = this.querySelector('[slot="media-gallery-viewer"]');
    const hoverImage = mediaViewer?.shadowRoot?.querySelector('#ids-image-hover') as HTMLElement;
    const currentSrc = mediaViewer?.getAttribute(attributes.SRC) || '';

    if (hoverImage && currentSrc) {
      hoverImage.setAttribute(attributes.SRC, currentSrc);
    }
  }

  /**
   * Apply video thumbnails
   */
  #applyVideoThumbClass() {
    const thumbnails = this.thumbnails;

    const createOverlayIcon = () => {
      const icon = document.createElement('ids-icon');
      icon.classList.add('video-play-button');
      icon.setAttribute('icon', 'play-button-filled');
      icon.setAttribute('size', 'large');
      icon.setAttribute('color', '#ffffff');
      Object.assign(icon.style, {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none'
      });
      return icon;
    };

    const createOverlayBackground = () => {
      const bg = document.createElement('div');
      bg.classList.add('video-play-bg');
      Object.assign(bg.style, {
        width: '100px',
        height: '100px',
        backgroundColor: 'var(--ids-color-black-30)',
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        borderRadius: '50%',
        pointerEvents: 'none'
      });
      return bg;
    };

    thumbnails?.forEach((thumb: HTMLElement | any) => {
      const type = thumb.getAttribute('data-type');
      const img = thumb.querySelector('img');

      if (type === 'video' && img && !thumb.querySelector('.video-play-button')) {
        thumb.classList.add('video-thumb');
        thumb.style.position = 'relative';

        Object.assign(img.style, {
          width: '100%',
          height: '100%',
          objectFit: 'cover'
        });

        thumb.appendChild(createOverlayBackground());
        thumb.appendChild(createOverlayIcon());
      }
    });
  }

  /**
   * Updates the gallery counter
   * @param {number} index thumbnail image index
   */
  #updateGalleryCounter(index: number) {
    const label = this.shadowRoot?.querySelector('#media-gallery-counter');
    const total = this.thumbnails?.length || 0;
    if (label) {
      label.textContent = `${index + 1} of ${total}`;
      (label as HTMLElement).style.direction = 'ltr';
    }
  }

  /**
   * Check if the current screen size is mobile
   * @returns {boolean} true if mobile screen size
   */
  #isMobileScreen(): boolean {
    return window.matchMedia(`(max-width: ${breakpoints.md})`).matches;
  }

  /**
   * Check if hover zoom should be disabled based on mobile settings
   * @returns {boolean} true if hover zoom should be disabled
   */
  #shouldDisableHoverZoom(): boolean {
    return this.disableMobileZoom && (this.#isMobileScreen() || this.#isTouchDevice());
  }

  /**
   * Check if the device supports touch
   * @returns {boolean} true if touch device
   */
  #isTouchDevice(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /**
   * Get double-tap threshold with accessibility support
   * @returns {number} threshold in milliseconds
   */
  #getDoubleTapThreshold(): number {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return prefersReducedMotion ? 500 : 300;
  }

  /**
   * Check if position has changed
   * @param {object} params object containing position and prevPos
   * @param {number} params.pos position
   * @param {number} params.prevPos previous position
   * @returns {boolean} true if position has changed
   */
  #hasPositionChanged({ pos, prevPos }: any) {
    return pos !== prevPos;
  }

  /**
   * Check if value is in range
   * @param {object} params object containing scale attributes
   * @param {number} params.scale scale
   * @param {number} params.minScale minimum scale
   * @param {number} params.maxScale maximum scale
   * @returns {number} scale
   */
  #valueInRange({ minScale, maxScale, scale }: any) {
    return scale <= maxScale && scale >= minScale;
  }

  /**
   * Returns a function to calculate the new translate value based on
   * current and previous positions, but only when the scale is within
   * allowed bounds and the position has changed.
   * @param {object} params object containing scale params
   * @param {number} params.minScale - Minimum scale allowed
   * @param {number} params.maxScale - Maximum scale allowed
   * @param {number} params.scale - Current zoom scale
   * @returns {Function} A function that takes position data and returns the updated translate value
   */
  #getTranslate = ({ minScale, maxScale, scale }: any) => ({ pos, prevPos, translate }: any) => (
    this.#valueInRange({ minScale, maxScale, scale })
    && this.#hasPositionChanged({ pos, prevPos }) ? translate + (pos - prevPos * scale) * (1 - 1 / scale) : translate
  );

  /**
   * Calculates a new scale value based on zoom delta and step,
   * clamped between the provided min and max scale values.
   * @param {object} params object containing scale params
   * @param {number} params.scale - Current scale
   * @param {number} params.minScale - Minimum allowed scale
   * @param {number} params.maxScale - Maximum allowed scale
   * @param {number} params.deltaScale - Scale delta (e.g. 1 or -1)
   * @param {number} params.zoomStep - Step increment per zoom action
   * @returns {[number, number]} Tuple of [previous scale, new scale]
   */
  #getScale({
    scale,
    minScale,
    maxScale,
    deltaScale,
    zoomStep = 0.2
  }: any) {
    let newScale = scale + deltaScale * zoomStep;
    newScale = Math.max(minScale, Math.min(newScale, maxScale));
    return [scale, newScale];
  }

  /**
   * Constructs a CSS transform matrix string for the given scale and translation values.
   * @param {object} params object containing matrix params
   * @param {number} params.scale - Current scale
   * @param {number} params.translateX - Horizontal translation
   * @param {number} params.translateY - Vertical translation
   * @returns {string} CSS matrix transform string
   */
  #getMatrix({ scale, translateX, translateY }: any) {
    return `matrix(${scale}, 0, 0, ${scale}, ${translateX}, ${translateY})`;
  }

  /**
   * Applies a pan (translation) by modifying the transformation's translate values
   * and updating the element's transform style.
   * @param {object} params object containing pan params
   * @param {any} params.state - Current transformation state and target element
   * @param {number} params.originX - Horizontal pan amount
   * @param {number} params.originY - Vertical pan amount
   */
  #pan({ state, originX, originY }: any) {
    state.transformation.translateX += originX;
    state.transformation.translateY += originY;
    state.element.style.transform = this.#getMatrix({
      scale: state.transformation.scale,
      translateX: state.transformation.translateX,
      translateY: state.transformation.translateY
    });
  }

  /**
   * Returns an object with `panBy` and `panTo` methods for panning the element.
   * @param {object} state - The shared state object containing transformation data
   * @returns {{ panBy: Function, panTo: Function }} Pan controller methods
   */
  #makePan(state: any) {
    return {
      panBy: ({ originX, originY }: any) => this.#pan({ state, originX, originY }),
      panTo: ({ originX, originY, scale }: any) => {
        state.transformation.scale = scale;
        this.#pan({
          state,
          originX: originX - state.transformation.translateX,
          originY: originY - state.transformation.translateY
        });
      },
    };
  }

  /**
   * Returns a `zoom` method that adjusts scale and transform origin
   * based on either cursor position or the center of the element.
   * @param {object} state - The shared state object containing transformation data
   * @returns {{ zoom: Function }} Zoom controller method
   */
  #makeZoom(state: any) {
    return {
      zoom: ({ x = 0, y = 0, deltaScale }: any) => {
        const {
          minScale,
          maxScale,
          scaleSensitivity,
          zoomStep,
          zoomMode
        } = state;
        const [scale, newScale] = this.#getScale({
          scale: state.transformation.scale,
          deltaScale,
          minScale,
          maxScale,
          scaleSensitivity,
          zoomStep
        });

        let originX = 0;
        let originY = 0;
        let translateX;
        let translateY;

        if (zoomMode === 'cursor') {
          const { left, top } = state.element.getBoundingClientRect();
          originX = x - left;
          originY = y - top;
          const newOriginX = originX / scale;
          const newOriginY = originY / scale;
          const translate = this.#getTranslate({ scale, minScale, maxScale });

          translateX = translate({
            pos: originX,
            prevPos: state.transformation.originX,
            translate: state.transformation.translateX
          });

          translateY = translate({
            pos: originY,
            prevPos: state.transformation.originY,
            translate: state.transformation.translateY
          });
          state.element.style.transformOrigin = `${newOriginX}px ${newOriginY}px`;
        } else {
          originX = state.element.offsetWidth / 2;
          originY = state.element.offsetHeight / 2;
          state.element.style.transformOrigin = 'center center';
          translateX = state.transformation.translateX;
          translateY = state.transformation.translateY;
        }

        state.element.style.transform = this.#getMatrix({ scale: newScale, translateX, translateY });

        state.transformation = {
          ...state.transformation,
          originX,
          originY,
          translateX,
          translateY,
          scale: newScale
        };
      }
    };
  }

  /**
   * Resets the zoom and pan to the default state (scale = 1, translation = 0).
   * Disables zoom-out and zoom-fit buttons and resets the cursor.
   * @param {object} instance - The zoom/pan controller instance
   * @param {Element} image - The image element to reset
   */
  #resetZoomAndPan(instance: any, image: Element | any) {
    instance.panTo({
      originX: 0,
      originY: 0,
      scale: 1,
    });
    this.#isDragging = false;
    image.style.cursor = 'default';
    this.#zoomOutButton.disabled = true;
    this.#zoomFitButton.disabled = true;
  }

  /**
   * Creates and returns a zoom/pan controller instance for a specific element.
   * @param {object} params object containing zoom/pan params
   * @param {Element} params.element - Target DOM element to zoom/pan
   * @param {number} params.minScale - Minimum allowed zoom scale
   * @param {number} params.maxScale - Maximum allowed zoom scale
   * @param {number} [params.scaleSensitivity] - (Unused in linear zoom but kept for compatibility)
   * @param {number} [params.zoomStep] - Amount to change scale on each zoom action
   * @param {string} [params.zoomMode] - Zoom mode: `'center'` or `'cursor'`
   * @returns {{ zoom: Function, panBy: Function, panTo: Function, getScale: Function, isZoomed: Function }} Zoom/pan API
   */
  createZoomPanController({
    minScale,
    maxScale,
    element,
    scaleSensitivity = 10,
    zoomStep = 0.2,
    zoomMode = 'center' // 'center' or 'cursor'
  }: any) {
    const state = {
      element,
      minScale,
      maxScale,
      scaleSensitivity,
      zoomStep,
      zoomMode,
      transformation: {
        originX: 0,
        originY: 0,
        translateX: 0,
        translateY: 0,
        scale: 1
      }
    };

    return {
      ...this.#makeZoom(state),
      ...this.#makePan(state),
      getScale: () => state.transformation.scale,
      isZoomed: () => state.transformation.scale > 1
    };
  }
}
